import data from "../data/dados.json";
import {
  getTurmasCache,
  getInstituicoesCache,
  getUsuariosCache,
} from "../services/dataCache";

export const STORAGE_USUARIOS = "usuarios_override";
export const STORAGE_TURMAS = "turmas_override";
export const STORAGE_USUARIOS_DISCIPLINAS = "usuarios_disciplinas_override";
export const STORAGE_DISCIPLINAS_KEYS = [
  "turmas_override",
  "disciplinas_override",
  "disciplinas",
  "turmas",
];

export function safeJsonParse(value, fallback) {
  try {
    return JSON.parse(value) ?? fallback;
  } catch {
    return fallback;
  }
}

export function readStorageObject(key) {
  return safeJsonParse(localStorage.getItem(key), {});
}

export function writeStorageObject(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

export function mergeById(baseList, overrideMap) {
  const map = {};

  (Array.isArray(baseList) ? baseList : []).forEach((item) => {
    map[String(item.id)] = { ...item };
  });

  Object.entries(overrideMap || {}).forEach(([id, patch]) => {
    map[String(id)] = {
      ...(map[String(id)] || {}),
      ...patch,
    };
  });

  return Object.values(map);
}

export function getInstituicoesMescladas() {
  const apiCache = getInstituicoesCache();
  if (apiCache.length > 0) return apiCache;
  return Array.isArray(data.instituicoes) ? data.instituicoes : [];
}

export function getUsuariosMesclados() {
  const apiCache = getUsuariosCache();
  if (apiCache.length > 0) return apiCache;
  const overrides = readStorageObject(STORAGE_USUARIOS);
  return mergeById(data.usuarios || [], overrides);
}

export function getDisciplinasMescladas() {
  const apiCache = getTurmasCache();
  if (apiCache.length > 0) {
    // Adapta turma backend → shape legado (disciplina) usado pela UI
    return apiCache.map((t) => ({
      id: t.id,
      nome: t.nome,
      complemento: t.complemento,
      tipo: t.tipo,
      instituicaoId: t.instituicaoId,
      alunosCount: t.alunosCount ?? 0,
    }));
  }
  const overrides = readStorageObject(STORAGE_TURMAS);
  return mergeById(data.disciplinas || [], overrides);
}

export function getEventosMesclados() {
  const overrides = readStorageObject("eventos_override");
  return mergeById(data.eventos || [], overrides);
}

export function getUsuariosDisciplinasOverride() {
  return readStorageObject(STORAGE_USUARIOS_DISCIPLINAS);
}

export function getUsuarioById(id) {
  return (
    getUsuariosMesclados().find((u) => Number(u.id) === Number(id)) || null
  );
}

export function getInstituicaoById(id) {
  if (id === undefined || id === null || id === "") return null;
  // Compara como string pra suportar Guid (backend) e número (mock legado)
  return (
    getInstituicoesMescladas().find((i) => String(i.id) === String(id)) || null
  );
}

export function getUsuarioLogado() {
  const usuarioStorage = safeJsonParse(localStorage.getItem("usuario"), null);

  if (!usuarioStorage?.id) return null;

  // Adapta payload backend → shape esperado pela UI legada
  const adaptado = {
    ...usuarioStorage,
    faculdadeId: usuarioStorage.faculdadeId || usuarioStorage.instituicaoId,
    tipo: String(usuarioStorage.tipo || "").toLowerCase(),
  };

  // Tenta enriquecer com cache de usuários (mock); senão retorna o adaptado
  const fromCache = getUsuariosMesclados().find(
    (u) => String(u.id) === String(usuarioStorage.id),
  );

  return fromCache
    ? {
        ...adaptado,
        ...fromCache,
        faculdadeId: adaptado.faculdadeId,
        tipo: adaptado.tipo,
      }
    : adaptado;
}

function normalizarIds(ids, idAntigo) {
  if (Array.isArray(ids)) {
    return ids.map(Number).filter(Boolean);
  }

  if (idAntigo !== undefined && idAntigo !== null && idAntigo !== "") {
    return [Number(idAntigo)].filter(Boolean);
  }

  return [];
}

export function getDisciplinasPermitidas(user) {
  if (!user) return [];

  const disciplinasMescladas = getDisciplinasMescladas();
  const usuariosDisciplinasOverride = getUsuariosDisciplinasOverride();

  const userId = Number(user.id);
  const faculdadeId = Number(user.faculdadeId);

  const idsBaseUsuario = Array.isArray(user.disciplinas)
    ? user.disciplinas.map(Number)
    : [];

  const idsOverrideUsuario = Array.isArray(
    usuariosDisciplinasOverride[String(user.id)],
  )
    ? usuariosDisciplinasOverride[String(user.id)].map(Number)
    : [];

  const idsUsuario = [...new Set([...idsBaseUsuario, ...idsOverrideUsuario])];

  const disciplinasDaFaculdade = disciplinasMescladas.filter(
    (d) => Number(d.instituicaoId) === faculdadeId,
  );

  return disciplinasDaFaculdade.filter((d) => {
    const professorIds = normalizarIds(d.professorIds, d.professorId);
    const responsavelIds = normalizarIds(d.responsavelIds, d.responsavelId);
    const coordenadorIds = normalizarIds(d.coordenadorIds, d.coordenadorId);
    const alunoIds = normalizarIds(d.alunoIds, d.alunoId);

    const pertenceNovoModelo =
      professorIds.includes(userId) ||
      responsavelIds.includes(userId) ||
      coordenadorIds.includes(userId) ||
      alunoIds.includes(userId);

    const pertenceModeloAntigo = idsUsuario.includes(Number(d.id));

    if (user.tipo === "coordenador") {
      return (
        coordenadorIds.includes(userId) ||
        Number(d.criado_por) === userId ||
        pertenceModeloAntigo ||
        Number(d.instituicaoId) === faculdadeId
      );
    }

    if (user.tipo === "professor") {
      return professorIds.includes(userId) || pertenceModeloAntigo;
    }

    if (user.tipo === "responsavel") {
      return (
        responsavelIds.includes(userId) ||
        alunoIds.includes(userId) ||
        pertenceModeloAntigo
      );
    }

    if (user.tipo === "aluno") {
      return alunoIds.includes(userId) || pertenceModeloAntigo;
    }

    return pertenceNovoModelo || pertenceModeloAntigo;
  });
}

export function saveOverrideById(storageKey, id, patch) {
  const overrides = readStorageObject(storageKey);

  overrides[String(id)] = {
    ...(overrides[String(id)] || {}),
    ...patch,
  };

  writeStorageObject(storageKey, overrides);
  notifyDataChanged();
}

export function notifyDataChanged() {
  window.dispatchEvent(new Event("app:data-changed"));
}

export function setDisciplinaAtual(id) {
  if (id === null || id === undefined || id === "") {
    localStorage.removeItem("disciplinaAtualId");
  } else {
    localStorage.setItem("disciplinaAtualId", String(id));
  }

  window.dispatchEvent(new Event("disciplinaAtual:changed"));
  notifyDataChanged();
}
